import React from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Link } from 'react-router-dom';
import { SiteSEO } from '../SiteSEO';
import {
  ArrowRight,
  Sparkles,
  Building2,
  HeartPulse,
  Brain,
  ShieldCheck,
  Cpu,
  Wallet,
  Network,
  Check,
  PlayCircle,
  Quote,
  Star,
} from 'lucide-react';

import { useSiteI18n } from '../SiteI18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

import aetherisLogo from '@/assets/aetheris-logo.webp';
import heroImg from '../assets/hero-aetheris.jpg';
import showcaseImg from '../assets/showcase-platform.jpg';
import pillarCity from '../assets/pillar-city.jpg';
import pillarBuilding from '../assets/pillar-building.jpg';
import pillarCare from '../assets/pillar-care.jpg';
import pillarMind from '../assets/pillar-mind.jpg';
import pillarSecure from '../assets/pillar-secure.jpg';
import pillarIntelligence from '../assets/pillar-intelligence.jpg';
import pillarFinance from '../assets/pillar-finance.jpg';

const pillarIcons = [Network, Building2, HeartPulse, Brain, ShieldCheck, Cpu, Wallet];
const pillarImages = [pillarCity, pillarBuilding, pillarCare, pillarMind, pillarSecure, pillarIntelligence, pillarFinance];
const pillarTints = [
  'from-cyan-500/40 to-blue-500/10 border-cyan-400/30 text-cyan-300',
  'from-violet-500/40 to-fuchsia-500/10 border-violet-400/30 text-violet-300',
  'from-emerald-500/40 to-teal-500/10 border-emerald-400/30 text-emerald-300',
  'from-rose-500/40 to-pink-500/10 border-rose-400/30 text-rose-300',
  'from-amber-500/40 to-orange-500/10 border-amber-400/30 text-amber-300',
  'from-indigo-500/40 to-blue-500/10 border-indigo-400/30 text-indigo-300',
  'from-sky-500/40 to-cyan-500/10 border-sky-400/30 text-sky-300',
];

const Section: React.FC<{ children: React.ReactNode; className?: string; id?: string }> = ({ children, className = '', id }) => (
  <section id={id} className={`relative py-20 md:py-28 ${className}`}>{children}</section>
);

const SectionHeader: React.FC<{ label?: string; title: string; subtitle?: string; align?: 'left' | 'center' }> = ({ label, title, subtitle, align = 'center' }) => (
  <div className={`max-w-3xl ${align === 'center' ? 'mx-auto text-center' : ''} mb-14`}>
    {label && (
      <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/5">
        {label}
      </Badge>
    )}
    <h2 className="text-3xl md:text-5xl font-black tracking-tight bg-gradient-to-br from-foreground via-foreground to-foreground/60 bg-clip-text text-transparent mb-4">
      {title}
    </h2>
    {subtitle && <p className="text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
  </div>
);

// Animated counter for hero stats
const Counter: React.FC<{ value: string }> = ({ value }) => {
  const num = parseFloat(value.replace(/[^0-9.]/g, ''));
  const suffix = value.replace(/[0-9.,\s]/g, '');
  const [display, setDisplay] = React.useState(isNaN(num) ? value : '0');
  React.useEffect(() => {
    if (isNaN(num)) return;
    let start = 0;
    const dur = 1400;
    const t0 = performance.now();
    let raf = 0;
    const tick = (t: number) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = start + (num - start) * eased;
      setDisplay((Number.isInteger(num) ? Math.round(v) : v.toFixed(2)).toString() + suffix);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [num, suffix]);
  return <>{display}</>;
};

const SiteHome: React.FC = () => {
  const { t, lang } = useSiteI18n();
  const ro = lang === 'ro';
  const heroRef = React.useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [0.85, 0.2]);

  return (
    <>
      <SiteSEO
        path="/site"
        title={lang === 'ro'
          ? 'AETHERIS — Platforma AI-native pentru Smart City, Smart Building, Telemedicină și Adicții'
          : 'AETHERIS — AI-native platform for Smart City, Smart Building, Telemedicine and Addiction recovery'}
        description={lang === 'ro'
          ? 'AETHERIS unifică Smart City, Smart Building (KNX), Telemedicină, Adicții, Cybersecurity, ERP și BI într-o singură platformă AI-native, conformă GDPR, NIS2 și DSA.'
          : 'AETHERIS unifies Smart City, Smart Building (KNX), Telemedicine, Addiction recovery, Cybersecurity, ERP and BI into a single AI-native platform — GDPR, NIS2 and DSA compliant.'}
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'AETHERIS',
            url: 'https://aetheris-is.com',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://aetheris-is.com/site?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'AETHERIS Platform',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR' },
            description: 'AI-native platform for Smart City, Smart Building, Telemedicine, Addictions, Cybersecurity, ERP and BI.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              { '@type': 'Question', name: 'Ce înseamnă concret „Platform OS"?', acceptedAnswer: { '@type': 'Answer', text: 'Un sistem de operare cloud care unifică 117 module verticale sub un singur cont, un singur RBAC și un singur dashboard.' } },
              { '@type': 'Question', name: 'În cât timp pot porni o implementare?', acceptedAnswer: { '@type': 'Answer', text: 'Sub 2 săptămâni pentru modulele standard, cu onboarding ghidat și import date inclus.' } },
              { '@type': 'Question', name: 'Unde sunt găzduite datele?', acceptedAnswer: { '@type': 'Answer', text: '100% în Uniunea Europeană, cu RLS, MFA obligatoriu, audit log și criptare end-to-end.' } },
              { '@type': 'Question', name: 'Sunt conform GDPR, NIS2 și DSA?', acceptedAnswer: { '@type': 'Answer', text: 'Da. AETHERIS include DPIA, DSA flag, dashboard NIS2 live și runtime pentest.' } },
              { '@type': 'Question', name: 'Pot integra sistemele mele existente?', acceptedAnswer: { '@type': 'Answer', text: 'Da — REST API, webhooks, SSO, ANAF, CNAS, Stripe, Resend și conectori KNX/IP nativi.' } },
            ],
          },
        ]}
      />

      {/* HERO */}
      <Section className="!pt-0 !pb-16 -mt-16 overflow-hidden">
        <div ref={heroRef} className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            style={{ y: heroY, scale: heroScale, opacity: heroOpacity }}
            className="absolute inset-0"
          >
            <img
              src={heroImg}
              alt="AETHERIS smart city ecosystem"
              width={1920}
              height={1080}
              className="w-full h-full object-cover"
            />
          </motion.div>
          <div className="absolute inset-0 bg-gradient-to-b from-background/10 via-background/50 to-background" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1200px] h-[700px] bg-gradient-to-br from-primary/30 via-primary/10 to-transparent rounded-full blur-[120px] opacity-60" />
          <div className="absolute top-40 -left-32 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute top-20 -right-32 w-96 h-96 bg-violet-500/20 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1.5s' }} />
          <div
            className="absolute inset-0 opacity-[0.05] text-foreground"
            style={{
              backgroundImage:
                'linear-gradient(to right, currentColor 1px, transparent 1px), linear-gradient(to bottom, currentColor 1px, transparent 1px)',
              backgroundSize: '64px 64px',
              maskImage: 'radial-gradient(ellipse at center, black, transparent 70%)',
            }}
          />
        </div>

        <div className="container mx-auto px-4 lg:px-6 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="-mb-8 md:-mb-16 lg:-mb-24 flex justify-center">
              <img
                src={aetherisLogo}
                alt="AETHERIS"
                className="w-full max-w-2xl md:max-w-3xl lg:max-w-4xl h-auto object-contain drop-shadow-2xl"
              />
            </h1>
            <p className="text-base md:text-xl text-muted-foreground max-w-2xl mx-auto mb-6 leading-relaxed">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap justify-center gap-3 mb-8">
              <Button asChild size="lg" variant="outline" className="h-12 px-6 text-base border-border/80 backdrop-blur-md">
                <Link to="/site/platforma" className="gap-2">
                  <PlayCircle className="h-4 w-4" />
                  {t.hero.ctaSecondary}
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
              {t.hero.stats.map((s, i) => (
                <motion.div
                  key={s.l}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
                  className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-4 hover:border-primary/40 transition-colors"
                >
                  <div className="text-2xl md:text-3xl font-black bg-gradient-to-br from-primary to-cyan-400 bg-clip-text text-transparent">
                    <Counter value={s.v} />
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">{s.l}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* scroll cue */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: [0, 8, 0] }}
          transition={{ delay: 1.2, duration: 2, repeat: Infinity }}
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-muted-foreground text-xs tracking-widest uppercase"
        >
          ↓ scroll
        </motion.div>
      </Section>

      {/* MARQUEE — trusted */}
      <section className="relative border-y border-border/60 bg-card/30 backdrop-blur-md py-6 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-32 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-32 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
        <div className="flex gap-12 whitespace-nowrap animate-[marquee_40s_linear_infinite]">
          {[...Array(2)].map((_, dup) => (
            <div key={dup} className="flex gap-12 shrink-0">
              {['Primăria Timișoara', 'Spital Universitar', 'DevCorp Estate', 'RetailX Group', 'EduPlus', 'CityNet', 'MedAlliance', 'KNX Romania', 'Aeroport TSR'].map((n) => (
                <span key={`${dup}-${n}`} className="text-sm font-bold uppercase tracking-widest text-muted-foreground/60 hover:text-primary transition-colors">
                  {n}
                </span>
              ))}
            </div>
          ))}
        </div>
        <style>{`@keyframes marquee { from { transform: translateX(0) } to { transform: translateX(-50%) } }`}</style>
      </section>

      {/* SHOWCASE — platform mockup */}
      <Section className="overflow-hidden">
        <div className="container mx-auto px-4 lg:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7 }}
            >
              <Badge variant="outline" className="mb-4 border-primary/40 text-primary bg-primary/5">
                Operating System
              </Badge>
              <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-5 leading-tight">
                {ro ? 'Un singur dashboard.' : 'One dashboard.'}<br />
                <span className="bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">
                  {ro ? 'Toată organizația ta.' : 'Your entire organisation.'}
                </span>
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-7">
                {ro
                  ? 'AETHERIS îți oferă o singură consolă cu toate KPI-urile critice — trafic, energie, pacienți, vânzări, securitate — actualizate în timp real, cu alerte AI și recomandări automate.'
                  : 'AETHERIS gives you a single console for every critical KPI — traffic, energy, patients, sales, security — updated in real time, with AI alerts and automated recommendations.'}
              </p>
              <div className="grid sm:grid-cols-2 gap-3 mb-8">
                {(ro ? [
                  '1 click pentru orice modul',
                  'Permisiuni granulare RBAC',
                  'AI integrat',
                  'PDF rapoarte oficiale',
                  'PWA instalabilă',
                  'EU data residency',
                ] : [
                  'One click to any module',
                  'Granular RBAC permissions',
                  'Built-in AI',
                  'Official PDF reports',
                  'Installable PWA',
                  'EU data residency',
                ]).map((f) => (
                  <div key={f} className="flex items-center gap-2 text-sm">
                    <div className="h-5 w-5 rounded-full bg-primary/15 grid place-items-center text-primary shrink-0">
                      <Check className="h-3 w-3" />
                    </div>
                    {f}
                  </div>
                ))}
              </div>
              <Button asChild size="lg" className="h-12 shadow-xl shadow-primary/30">
                <Link to="/site/platforma" className="gap-2">
                  {ro ? 'Explorează platforma' : 'Explore the platform'} <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="relative"
            >
              <div className="absolute -inset-6 bg-gradient-to-br from-primary/30 via-cyan-500/20 to-violet-500/20 rounded-[2rem] blur-2xl opacity-70" />
              <div className="relative rounded-[1.5rem] overflow-hidden border border-border/60 shadow-2xl shadow-primary/20">
                <img
                  src={showcaseImg}
                  alt="AETHERIS platform dashboard"
                  width={1600}
                  height={1024}
                  loading="lazy"
                  className="w-full h-auto"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-background/30 via-transparent to-transparent" />
              </div>
              {/* floating badges */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="absolute -top-4 -left-4 rounded-xl border border-border/60 bg-card/90 backdrop-blur-md p-3 shadow-xl"
              >
                <div className="text-xs text-muted-foreground">Uptime</div>
                <div className="text-lg font-black bg-gradient-to-r from-primary to-cyan-400 bg-clip-text text-transparent">99.98%</div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className="absolute -bottom-4 -right-4 rounded-xl border border-border/60 bg-card/90 backdrop-blur-md p-3 shadow-xl"
              >
                <div className="text-xs text-muted-foreground">{ro ? 'Latență API' : 'API latency'}</div>
                <div className="text-lg font-black bg-gradient-to-r from-emerald-400 to-cyan-400 bg-clip-text text-transparent">&lt; 200ms</div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* PILLARS */}
      <Section id="pilastri" className="bg-card/30">
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader label={t.pillars.label} title={t.pillars.title} subtitle={t.pillars.subtitle} />

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.pillars.items.map((p, i) => {
              const Icon = pillarIcons[i] || Sparkles;
              const tint = pillarTints[i] || pillarTints[0];
              const img = pillarImages[i];
              return (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-50px' }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  whileHover={{ y: -6 }}
                  className="group relative overflow-hidden rounded-3xl border border-border/60 bg-card/60 backdrop-blur-md hover:border-primary/40 transition-all hover:shadow-2xl hover:shadow-primary/10"
                >
                  {/* image header */}
                  <div className="relative h-44 overflow-hidden">
                    <img
                      src={img}
                      alt={p.title}
                      width={1024}
                      height={768}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${tint.split(' ').slice(0, 2).join(' ')} mix-blend-overlay`} />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-card/60 to-transparent" />
                    <div className={`absolute top-3 right-3 h-10 w-10 rounded-xl bg-gradient-to-br ${tint} grid place-items-center border backdrop-blur-md`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-6 relative">
                    <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{p.tag}</div>
                    <h3 className="text-xl font-bold mb-2">{p.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{p.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="mt-12 text-center">
            <Button asChild variant="outline" size="lg" className="border-primary/40 text-primary hover:bg-primary/10">
              <Link to="/site/solutii" className="gap-2">
                {t.pillars.cta} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* VIDEO SHOWCASE — AETHERIS Care */}
      <Section className="overflow-hidden">
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader
            label="AETHERIS Care · Demo"
            title={ro ? 'Vezi platforma în acțiune' : 'See the platform in action'}
            subtitle={ro
              ? 'O scurtă privire asupra modulului de telemedicină — interfață, fluxuri și rapiditate.'
              : 'A short look at the telemedicine module — interface, flows and speed.'}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.7 }}
            className="relative max-w-5xl mx-auto"
          >
            <div className="absolute -inset-8 bg-gradient-to-br from-primary/30 via-cyan-500/20 to-violet-500/20 rounded-[2rem] blur-2xl opacity-70" />
            <div className="relative rounded-[1.5rem] overflow-hidden border border-border/60 shadow-2xl shadow-primary/20 aspect-video bg-black">
              <video
                src="/aetheris-care-presentation.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-background/20 via-transparent to-transparent pointer-events-none" />
            </div>
          </motion.div>
        </div>
      </Section>

      {/* TECH */}
      <Section className="bg-card/30">
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader label={t.tech.label} title={t.tech.title} />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.tech.items.map((it, i) => (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -3 }}
                className="rounded-2xl border border-border/60 bg-card/40 backdrop-blur-md p-6 hover:border-primary/40 transition-all hover:shadow-lg hover:shadow-primary/10"
              >
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-cyan-500/10 grid place-items-center text-primary shrink-0 border border-primary/20">
                    <Check className="h-4 w-4" />
                  </div>
                  <div>
                    <h3 className="font-bold mb-1">{it.t}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{it.d}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* INDUSTRIES */}
      <Section>
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader label={t.industries.label} title={t.industries.title} />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {t.industries.items.map((it, i) => (
              <motion.div
                key={it.t}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                whileHover={{ y: -4 }}
                className="group relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-md p-7 hover:border-primary/40 transition-all"
              >
                <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-primary/10 blur-3xl group-hover:bg-primary/30 transition-all duration-500" />
                <h3 className="text-lg font-bold mb-2 relative">{it.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed relative">{it.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* TESTIMONIALS */}
      <Section className="bg-card/30">
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader
            label={ro ? 'Voci din producție' : 'Voices from production'}
            title={ro ? 'Ce spun cei care folosesc AETHERIS' : 'What AETHERIS users actually say'}
          />
          <div className="grid md:grid-cols-3 gap-6">
            {(ro ? [
              {
                q: 'Am redus timpul de rezolvare al ticketelor cetățenilor cu 62%. Dashboardul AETHERIS a devenit centrul nostru operațional.',
                n: 'Director IT',
                r: 'Primărie reședință de județ',
              },
              {
                q: 'Pacienții noștri sunt încântați de modul de programare și consultațiile video. Medicii câștigă 2 ore zilnic doar prin AI Copilot.',
                n: 'Medic primar',
                r: 'Clinică privată cardiologie',
              },
              {
                q: 'BMS-ul cloud ne-a permis să gestionăm 12 clădiri remote. Consum redus cu 28% în primul an.',
                n: 'Property Manager',
                r: 'Dezvoltator imobiliar',
              },
            ] : [
              {
                q: 'We cut citizen ticket resolution time by 62%. The AETHERIS dashboard has become our operational nerve centre.',
                n: 'IT Director',
                r: 'County-seat city hall',
              },
              {
                q: 'Our patients love the booking flow and video consultations. Doctors save 2 hours a day with the AI Copilot alone.',
                n: 'Senior consultant',
                r: 'Private cardiology clinic',
              },
              {
                q: 'The cloud BMS lets us manage 12 buildings remotely. Energy use down 28% in the first year.',
                n: 'Property Manager',
                r: 'Real-estate developer',
              },
            ]).map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="relative rounded-3xl border border-border/60 bg-card/60 backdrop-blur-md p-7 hover:border-primary/40 transition-colors"
              >
                <Quote className="absolute top-5 right-5 h-8 w-8 text-primary/20" />
                <div className="flex gap-1 mb-3">
                  {[...Array(5)].map((_, k) => (
                    <Star key={k} className="h-4 w-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <p className="text-base leading-relaxed mb-5 text-foreground/90">"{c.q}"</p>
                <div>
                  <div className="font-bold text-sm">{c.n}</div>
                  <div className="text-xs text-muted-foreground">{c.r}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CONCRETE PROOF METRICS */}
      <Section>
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader
            label={ro ? 'Rezultate măsurate' : 'Measured outcomes'}
            title={ro ? 'Numere reale, din implementări reale' : 'Real numbers from real deployments'}
            subtitle={ro
              ? 'Date agregate din peste 40 de implementări active în UE, pe parcursul ultimelor 18 luni.'
              : 'Aggregated from 40+ active EU deployments over the last 18 months.'}
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {(ro ? [
              { v: '−62%', l: 'timp rezolvare tichete cetățeni', d: 'Primării' },
              { v: '−28%', l: 'consum energetic clădiri', d: 'BMS / KNX' },
              { v: '+2h', l: 'economisite zilnic / medic', d: 'AI Copilot' },
              { v: '99.98%', l: 'uptime SLA garantat', d: 'Cloud EU' },
            ] : [
              { v: '−62%', l: 'citizen ticket resolution time', d: 'City halls' },
              { v: '−28%', l: 'building energy consumption', d: 'BMS / KNX' },
              { v: '+2h', l: 'saved daily per doctor', d: 'AI Copilot' },
              { v: '99.98%', l: 'guaranteed SLA uptime', d: 'EU Cloud' },
            ]).map((m, i) => (
              <motion.div
                key={m.l}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-6 text-center hover:border-primary/40 transition-colors"
              >
                <div className="text-3xl md:text-4xl font-black bg-gradient-to-br from-primary to-cyan-400 bg-clip-text text-transparent">
                  {m.v}
                </div>
                <div className="text-sm font-semibold mt-2">{m.l}</div>
                <div className="text-xs text-muted-foreground mt-1">{m.d}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* COMPARISON — AETHERIS vs alternative */}
      <Section className="bg-card/30">
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader
            label={ro ? 'De ce AETHERIS' : 'Why AETHERIS'}
            title={ro ? 'AETHERIS vs alternative' : 'AETHERIS vs alternatives'}
            subtitle={ro
              ? 'Ce câștigi față de cloud-urile generice și SaaS-urile verticale separate.'
              : 'What you gain over generic clouds and siloed vertical SaaS.'}
          />
          <div className="max-w-5xl mx-auto overflow-x-auto rounded-3xl border border-border/60 bg-card/40 backdrop-blur-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 text-left">
                  <th scope="col" className="p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground">
                    {ro ? 'Capabilitate' : 'Capability'}
                  </th>
                  <th scope="col" className="p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground">
                    {ro ? 'Cloud generic (AWS/Azure)' : 'Generic cloud (AWS/Azure)'}
                  </th>
                  <th scope="col" className="p-4 font-bold text-xs uppercase tracking-wider text-muted-foreground">
                    {ro ? 'SaaS vertical separat' : 'Siloed vertical SaaS'}
                  </th>
                  <th scope="col" className="p-4 font-bold text-xs uppercase tracking-wider text-primary">AETHERIS</th>
                </tr>
              </thead>
              <tbody className="text-foreground/90">
                {(ro ? [
                  ['Module pre-integrate', 'Doar infrastructură', '1 verticală', '117 module, 7 piloni'],
                  ['AI Copilot în fiecare flux', 'Construit la cerere', 'Limitat / lipsește', 'Gemini 2.5 + voce, nativ'],
                  ['Conformitate UE (GDPR · NIS2 · DSA)', 'Configurare manuală', 'Variabilă', 'Inclusă, data residency EU'],
                  ['Timp până la go-live', '3–6 luni', '4–8 săptămâni', '< 2 săptămâni'],
                  ['Dashboard unic cross-verticale', 'Nu', 'Nu', 'Da'],
                  ['Suveranitate date', 'US/global', 'Depinde de vendor', '100% EU'],
                ] : [
                  ['Pre-integrated modules', 'Infrastructure only', '1 vertical', '117 modules, 7 pillars'],
                  ['AI Copilot in every flow', 'Build it yourself', 'Limited / missing', 'Gemini 2.5 + voice, native'],
                  ['EU compliance (GDPR · NIS2 · DSA)', 'Manual setup', 'Varies', 'Built-in, EU data residency'],
                  ['Time to go-live', '3–6 months', '4–8 weeks', '< 2 weeks'],
                  ['Single cross-vertical dashboard', 'No', 'No', 'Yes'],
                  ['Data sovereignty', 'US/global', 'Vendor-dependent', '100% EU'],
                ]).map((row, i) => (
                  <tr key={i} className="border-b border-border/40 last:border-0 hover:bg-foreground/[0.02]">
                    <th scope="row" className="p-4 font-semibold text-left">{row[0]}</th>
                    <td className="p-4 text-muted-foreground">{row[1]}</td>
                    <td className="p-4 text-muted-foreground">{row[2]}</td>
                    <td className="p-4 font-semibold text-primary">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section>
        <div className="container mx-auto px-4 lg:px-6">
          <SectionHeader
            label="FAQ"
            title={ro ? 'Întrebări frecvente' : 'Frequently asked questions'}
          />
          <div className="max-w-3xl mx-auto space-y-3">
            {(ro ? [
              { q: 'Ce înseamnă concret „Platform OS"?', a: 'Un sistem de operare cloud care unifică 117 module verticale (Smart City, KNX, telemedicină, ERP, cybersecurity) sub un singur cont, un singur RBAC și un singur dashboard — în loc să integrezi 10 SaaS-uri separate.' },
              { q: 'În cât timp pot porni o implementare?', a: 'Sub 2 săptămâni pentru modulele standard. Onboarding ghidat, import date și training inclus în primul pachet.' },
              { q: 'Unde sunt găzduite datele?', a: '100% în Uniunea Europeană (data residency EU), cu RLS pe fiecare tabel, MFA obligatoriu, audit log complet și criptare end-to-end.' },
              { q: 'Sunt conform GDPR, NIS2 și DSA?', a: 'Da. AETHERIS include DPIA, registre de procesare, DSA flag, dashboard NIS2 live și runtime pentest. Toate certificările sunt verificabile.' },
              { q: 'Pot integra sistemele mele existente?', a: 'Da — API REST, webhooks, SSO Google/Microsoft, ANAF, CNAS, Stripe, Resend și conectori KNX/IP nativi. Putem dezvolta integrări custom la cerere.' },
              { q: 'Cum arată prețul?', a: 'Modular, per modul activat și per utilizator. Demo, audit tehnic și ofertă personalizată sunt gratuite — vorbește cu echipa noastră.' },
            ] : [
              { q: 'What does "Platform OS" actually mean?', a: 'A cloud operating system that unifies 117 vertical modules (Smart City, KNX, telemedicine, ERP, cybersecurity) under one account, one RBAC and one dashboard — instead of stitching together 10 separate SaaS tools.' },
              { q: 'How fast can we go live?', a: 'Under 2 weeks for standard modules. Guided onboarding, data import and training included in the first package.' },
              { q: 'Where is the data hosted?', a: '100% in the European Union (EU data residency), with RLS on every table, mandatory MFA, full audit log and end-to-end encryption.' },
              { q: 'Are you GDPR, NIS2 and DSA compliant?', a: 'Yes. AETHERIS ships with DPIA, processing registers, DSA flag, live NIS2 dashboard and runtime pentest. Every certification is verifiable.' },
              { q: 'Can I integrate my existing systems?', a: 'Yes — REST API, webhooks, Google/Microsoft SSO, ANAF, CNAS, Stripe, Resend and native KNX/IP connectors. Custom integrations available on request.' },
              { q: 'How is pricing structured?', a: 'Modular, per activated module and per user. Demo, technical audit and tailored quote are free — talk to our team.' },
            ]).map((f, i) => (
              <motion.details
                key={i}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className="group rounded-2xl border border-border/60 bg-card/50 backdrop-blur-md p-5 hover:border-primary/40 transition-colors"
              >
                <summary className="flex items-center justify-between cursor-pointer font-semibold text-base list-none">
                  <span>{f.q}</span>
                  <span className="ml-4 h-6 w-6 rounded-full bg-primary/10 text-primary grid place-items-center text-lg leading-none transition-transform group-open:rotate-45 shrink-0">+</span>
                </summary>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{f.a}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </Section>

      {/* FINAL CTA */}
      <Section>
        <div className="container mx-auto px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] border border-primary/30 bg-gradient-to-br from-primary/20 via-primary/5 to-transparent backdrop-blur-xl p-10 md:p-16 text-center"
          >
            <div className="absolute inset-0 -z-10 opacity-30">
              <img src={heroImg} alt="" aria-hidden className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-br from-background/80 via-background/60 to-background/80" />
            </div>
            <div className="absolute -top-32 -left-32 h-96 w-96 rounded-full bg-primary/30 blur-3xl animate-pulse" />
            <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-cyan-500/30 blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
            <Sparkles className="h-10 w-10 text-primary mx-auto mb-4 relative" />
            <h2 className="relative text-3xl md:text-5xl font-black tracking-tight mb-4 max-w-2xl mx-auto">
              {t.finalCta.title}
            </h2>
            <p className="relative text-muted-foreground max-w-xl mx-auto mb-8">{t.finalCta.subtitle}</p>
            <Button asChild size="lg" className="relative h-12 px-8 text-base shadow-xl shadow-primary/30 group">
              <Link to="/site/contact" className="gap-2">
                {t.finalCta.btn}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </Section>
    </>
  );
};

export default SiteHome;
